import React, { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import fillcheck from "../../Asset/fill check.svg";
import { selectEmail } from "../../Redux/authSlice";
import { orgErrorCode, orgErrorMessage, useAcceptInviteMutation, useDeclineInviteMutation } from "../../Redux/orgApi";
import { useUsage } from "../../context/UsageContext";
import InlineSpinner from "../../pages/Team/components/InlineSpinner";

/** First letter of a team's name, for its avatar. */
export const OrgAvatar = ({ name, className = "size-10 text-[17px] rounded-[10px]" }) => (
  <span
    aria-hidden="true"
    className={`grid shrink-0 place-items-center bg-gradient-to-br from-[#3AB1F6] to-[#407BFF] font-semibold text-white ${className}`}
  >
    {(name || "?").trim().charAt(0).toUpperCase()}
  </span>
);

/**
 * "Join <team>?" — accepts (POST /api/org/invites/:id/accept) or declines
 * (POST /api/org/invites/:id/decline) a ZeroTyping Teams invite.
 */
const JoinTeamDialog = ({ invite, onClose, onAnswered }) => {
  const userEmail = useSelector(selectEmail);
  const { refreshDebounced: refreshPlan } = useUsage();
  const [acceptInvite, { isLoading: accepting }] = useAcceptInviteMutation();
  const [declineInvite, { isLoading: declining }] = useDeclineInviteMutation();
  const [error, setError] = useState(null);

  const busy = accepting || declining;

  // The invite was already answered elsewhere (e.g. the mobile app), revoked
  // or expired: treat it as answered so its notification stops offering it.
  const handleGone = (err) => {
    if (["INVITE_NOT_FOUND", "INVITE_EXPIRED"].includes(orgErrorCode(err))) onAnswered?.(invite);
  };
  const org = invite.orgName;
  const inviter = invite.invitedByName || "Someone";
  const email = invite.email || userEmail;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [busy, onClose]);

  const join = async () => {
    setError(null);
    try {
      await acceptInvite(invite.id).unwrap();
      onAnswered?.(invite);
      refreshPlan();
      toast.success(`You joined ${org}. Pro is on for every device.`);
      onClose();
    } catch (err) {
      handleGone(err);
      setError(orgErrorMessage(err, "Couldn't join the team. Please try again."));
    }
  };

  const decline = async () => {
    setError(null);
    try {
      await declineInvite(invite.id).unwrap();
      onAnswered?.(invite);
      toast.info(`You declined the invite from ${org}.`);
      onClose();
    } catch (err) {
      handleGone(err);
      setError(orgErrorMessage(err, "Couldn't decline the invite. Please try again."));
    }
  };

  const points = [
    "Pro starts right away on web, desktop, Android and iOS",
    `${org} pays. You'll never be asked to.`,
    "Leave any time from Manage Subscription",
  ];

  return (
    <div
      className="fixed inset-0 z-[220] flex items-center justify-center bg-black/30 px-4 py-6"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="join-team-title"
        className="relative w-full max-w-[520px] rounded-2xl bg-white p-5 font-inter shadow-[0_20px_60px_rgba(0,0,0,0.18)] sm:p-6"
      >
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          aria-label="Close"
          className="absolute right-4 top-4 rounded-md p-1 text-[#111827] transition hover:bg-[#F3F4F6] disabled:opacity-50"
        >
          <FiX className="size-5" />
        </button>

        <div className="flex items-center gap-3 pr-8">
          <OrgAvatar name={org} className="size-10 rounded-[10px] text-[17px] sm:size-11 sm:text-[18px]" />
          <div className="min-w-0">
            <h2 id="join-team-title" className="break-words text-[18px] font-semibold leading-tight text-[#111827] sm:text-[20px]">
              Join {org}?
            </h2>
            <p className="mt-0.5 break-words text-[12px] text-[#6B7280] sm:text-[13px]">
              {inviter} invited {email} to ZT Teams.
            </p>
          </div>
        </div>

        <ul className="mt-4 flex flex-col gap-2">
          {points.map((point) => (
            <li key={point} className="flex items-start gap-2 text-[13px] text-[#111827] sm:text-[14px]">
              <img src={fillcheck} alt="" aria-hidden="true" className="mt-px size-4 shrink-0" />
              <span>{point}</span>
            </li>
          ))}
        </ul>

        <div className="mt-4 grid grid-cols-1 gap-4 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4 sm:grid-cols-2">
          <div>
            <h3 className="text-[12px] font-semibold text-[#111827] sm:text-[13px]">What {org}&apos;s admins can see</h3>
            <p className="mt-1 text-[12px] leading-relaxed text-[#6B7280] sm:text-[13px]">
              Your name, email, role and the date you joined.
            </p>
          </div>
          <div>
            <h3 className="text-[12px] font-semibold text-[#111827] sm:text-[13px]">What they never see</h3>
            <p className="mt-1 text-[12px] leading-relaxed text-[#6B7280] sm:text-[13px]">
              Your transcripts, notes, prompts or dictionary words.
            </p>
          </div>
        </div>

        {error && (
          <p role="alert" className="mt-3 rounded-lg border border-[#F5C2C3] bg-[#FDECEC] px-3 py-2 text-[12px] font-medium text-[#B42318] sm:text-[13px]">
            {error}
          </p>
        )}

        <div className="mt-4 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={decline}
            disabled={busy}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#E5E7EB] px-3 text-[13px] font-semibold text-[#111827] transition hover:bg-[#D9DCE1] disabled:cursor-not-allowed disabled:opacity-60 sm:text-[14px]"
          >
            {declining && <InlineSpinner />}
            {declining ? "Declining…" : "Decline"}
          </button>
          <button
            type="button"
            onClick={join}
            disabled={busy}
            className="inline-flex h-10 min-w-0 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] px-3 text-[13px] font-semibold text-white transition hover:from-[#3AA6E4] hover:to-[#4175EB] disabled:cursor-not-allowed disabled:opacity-70 sm:text-[14px]"
          >
            {accepting && <InlineSpinner />}
            <span className="truncate">{accepting ? "Joining…" : `Join ${org}`}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default JoinTeamDialog;
